import React from 'react';
import includes from 'lodash/includes';

import {
    trackEventFromState,
    trackEventFromStateOnPageUnload,
} from '@eb/site-analytics';

import {
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK,
    SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE,
} from '@eb/social-login-facebook';

import { AlertChunky } from '@eb/eds-iconography';

import { parseFormPostData } from '../api/utils';
import { socialLogIn as socialLogInApi } from '../api';

import {
    GA_AUTH_CATEGORY,
    GA_AUTH_CHECKOUT_CATEGORY,
    GA_SOCIAL_SIGNUP_EVENT,
    GA_FB_LOGIN_PERMISSIONS_NOT_GRANTED,
    GA_FB_LOGIN_ACCOUNT_CREATED,
    EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR,
} from '../constants';
import { GA_SIWA_POPUP_CLOSED } from '../constants/analytics';
import { ONLY_SOCIAL_LOGIN_ERROR_MESSAGE } from '../constants/messages';
import {
    GA_FACEBOOK,
    GA_FB_LOGIN_PERMISSIONS_GRANTED,
} from '../constants/analytics';

import { trackLoginSuccessful, trackLoginSuccessOneStep } from './gaTracking';

import { notifyParent } from '../utils';

import {
    getLogInFormData,
    getSocialLogInFormData,
    getSocialSignUpFormData,
    shouldOnboardingRedirect,
} from './utils';

import {
    submitSuccessRedirect,
    handleOnError,
    showAuthNotification,
} from './auth';

import { handleAppleLoginAttempt } from './appleLogin';

// Facebook handlers
const trackFBLoginSuccess = (state, response, actionCategory) => {
    if (response['new_user']) {
        trackEventFromState(state, {
            action: GA_FB_LOGIN_ACCOUNT_CREATED,
            category: actionCategory ? actionCategory : GA_AUTH_CATEGORY,
        });
        trackLoginSuccessful({
            sourceProvider: GA_FACEBOOK,
            state,
        });
    } else {
        trackLoginSuccessOneStep({
            sourceProvider: GA_FACEBOOK,
            state,
            gaCategory: actionCategory,
        });
    }
};

export const handleLoginSuccess = ({ state, response, dispatch }) => {
    let redirectUrl = response['redirect_url'];

    if (response['new_user'] && shouldOnboardingRedirect(state)) {
        redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
    }

    dispatch(submitSuccessRedirect(redirectUrl));
};

/**
 * Called when coming back from
 * * Facebook button > checked
 * * Apple button > checked
 * * Old flow
 */
export const socialLogInUser = (socialProvider, data = {}) => (
    dispatch,
    getState,
) => {
    const state = getState();

    // SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE constant is not working in qa for some reason
    if (socialProvider === 'apple') {
        return handleAppleLoginAttempt({
            socialProvider,
            data,
            getState,
            dispatch,
        });
    } else {
        return (
            socialLogInApi(
                parseFormPostData(getSocialLogInFormData(data, getState)),
            )
                .then((response) => {
                    if (
                        socialProvider ===
                        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK
                    ) {
                        trackFBLoginSuccess(state, response);

                        handleLoginSuccess({ state, response, dispatch });
                    } else if (
                        socialProvider ===
                        SOCIAL_EXTERNAL_IDENTITY_PROVIDER_GOOGLE
                    ) {
                        handleLoginSuccess({ state, response, dispatch });

                        // TODO: Here we should dispatch the action to track data on analytics
                        // We should also create a generic method to dispatch metrics
                    }
                })
                // User may be found in the lookup call or have failed
                .catch(handleOnError(dispatch, state, data))
        );
    }
};

export const socialLogInCheckoutUser = (socialProvider, data) => (
    dispatch,
    getState,
) => {
    const state = getState();

    return (
        socialLogInApi(
            parseFormPostData(getSocialLogInFormData(data, getState)),
        )
            // Send tracking data first so we don't lose it when we close the
            // iframe inside of checkoutWidget
            .then((response) => {
                return response;
            })
            .then((response) => {
                trackFBLoginSuccess(state, response, GA_AUTH_CHECKOUT_CATEGORY);
                notifyParent('checkoutLoginSuccessful');
            })
            .catch(
                handleOnError(dispatch, state, data, GA_AUTH_CHECKOUT_CATEGORY),
            )
    );
};

/* Needed for SIWA, returning to /signin when the email belongs to a soft
 * account there is not a real api call that fails, but we need to trigger
 * the same `onError` that Facebook returns in the same scenario.
 */
export const socialLogInSoftUser = () => (dispatch, getState) => {
    const state = getState();

    handleOnError(
        dispatch,
        state,
    )({ name: EXTERNAL_USER_EMAIL_USED_ON_SOFT_ACCOUNT_ERROR });
};

export const socialLogFacebookUser = () => (dispatch, getState) => {
    dispatch(
        showAuthNotification({
            message: ONLY_SOCIAL_LOGIN_ERROR_MESSAGE,
            iconType: <AlertChunky />,
        }),
    );
};

export const socialLogInUserNotAuthorized = (socialProvider, data) => (
    dispatch,
    getState,
) => {
    const state = getState();

    if (socialProvider === SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK) {
        if (data && includes(['unknown', 'not_authorized'], data.status)) {
            trackEventFromState(state, {
                action: GA_FB_LOGIN_PERMISSIONS_NOT_GRANTED,
                category: GA_AUTH_CATEGORY,
            });
        }
    }
    // SOCIAL_EXTERNAL_IDENTITY_PROVIDER_APPLE constant is not working in qa for some reason
    if (socialProvider === 'apple') {
        if (data && includes(['popup_closed_by_user'], data.status)) {
            trackEventFromState(state, {
                action: GA_SIWA_POPUP_CLOSED,
                category: GA_AUTH_CATEGORY,
            });
        }
    }
};

export const socialLogInUserAuthorized = (socialProvider) => (
    dispatch,
    getState,
) => {
    const state = getState();
    if (socialProvider === SOCIAL_EXTERNAL_IDENTITY_PROVIDER_FACEBOOK) {
        trackEventFromState(state, {
            action: GA_FB_LOGIN_PERMISSIONS_GRANTED,
            category: GA_AUTH_CATEGORY,
        });
    }
};

export const socialSignUpUser = (data) => (dispatch, getState) => {
    const state = getState();

    return (
        socialLogInApi(parseFormPostData(getSocialSignUpFormData(data, state)))
            .then((response) => {
                trackFBLoginSuccess(state, response);
                let redirectUrl = response['redirect_url'];

                if (shouldOnboardingRedirect(state)) {
                    redirectUrl = `/u/interests/?onboarding_referrer=${redirectUrl}`;
                }
                return dispatch(submitSuccessRedirect(redirectUrl));
            })
            .then(() =>
                trackEventFromStateOnPageUnload(state, {
                    action: GA_SOCIAL_SIGNUP_EVENT,
                    category: GA_AUTH_CATEGORY,
                }),
            )
            // pass returned data so it can be read and used to make decisions
            .catch(handleOnError(dispatch, state, data))
    );
};

/**
 * Calling social_action but handling an empty response for it because
 * we only want to store the data on redis to allow other actions like
 * * Forgot password
 * @param {string} email
 * @param {string} accessToken
 */
export const storeSocialDataOnRedis = ({
    email,
    accessToken,
    sourceProvider,
}) => () =>
    socialLogInApi(parseFormPostData({ email, accessToken, sourceProvider }))
        .then(() => {
            Promise.resolve();
        })
        .catch(() => {
            Promise.resolve();
        });
